import { z } from 'zod';
import { dataSchema, uuidSchema } from './comum.ts';
import {
  obraPrincipalDoTitulo,
  obrasDoTitulo,
  type RateioBrutoContasReceber,
} from './relatorio-contas-receber.ts';
import {
  COMPARAR_TEXTO_BAIXAS,
  contaCorrenteResumoDeBaixasSchema,
  quebrarPorDataDaBaixa,
  resumoPorContaCorrente,
  somarValoresDaBaixa,
  totaisDeBaixasSchema,
  valoresDaBaixaBruta,
  valoresDaBaixaSchema,
  type ValoresBrutosDaBaixa,
} from './relatorio-baixas.ts';

/**
 * Relatório de recebimentos (`REC·04`).
 *
 * O espelho do `PAG·04` do outro lado do caixa: o que **entrou** nas contas
 * correntes por baixa de título a receber, baixa por baixa, **quebrado por
 * empresa** e em ordem de **data de recebimento**, com subtotal das cinco
 * colunas de valor a cada data. O `REC·03` responde o que ainda se tem a
 * receber; este, o que já se recebeu.
 *
 * ## De onde vem cada linha
 *
 * De `baixas_contas_receber`: **uma baixa, uma linha**. A estornada fica de
 * fora (o dinheiro não entrou), e as três ações entram — em todas o dinheiro
 * passa pela conta corrente. As mesmas regras do `PAG·04`.
 *
 * ## Obra e contrato
 *
 * Os do **título**, pela regra do `REC·03`: o contrato é chave do título (um ou
 * nenhum), e a obra é a **principal** do rateio (`contas_receber_rateio`) — a de
 * maior índice somado, empate pelo nome —, com "(+N)" quando há outras.
 * `contas_receber.obra_id` não é lido: diz quem gerou o título, e não de que
 * obra é a receita (decisão 22, item 4).
 *
 * ## Emissão
 *
 * O lançamento do título (`contas_receber.data_lancamento`), pela regra do
 * `REC·03`: o receber não tem documento fiscal.
 */

/* -------------------------------------------------------------------------- */
/* Teto                                                                       */
/* -------------------------------------------------------------------------- */

/** Teto de baixas por consulta — o do `PAG·04`, e pela mesma razão. */
export const LIMITE_BAIXAS_RELATORIO_RECEBIMENTOS = 5000;

/* -------------------------------------------------------------------------- */
/* Consulta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros do relatório. **Todos opcionais**, e a ausência é "sem recorte".
 *
 * - `empresaId` é a empresa **credora** do título (`contas_receber.empresa_id`).
 * - `contaCorrenteId` é a conta em que o dinheiro entrou.
 * - O período é o da **data do recebimento** (`data_movimentacao` da baixa),
 *   inclusivo nas duas pontas.
 */
export const relatorioRecebimentosQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    clienteId: uuidSchema.optional(),
    contaCorrenteId: uuidSchema.optional(),
    recebimentoDe: dataSchema.optional(),
    recebimentoAte: dataSchema.optional(),
  })
  .strict();

export type RelatorioRecebimentosQuery = z.infer<typeof relatorioRecebimentosQuerySchema>;
/** O que o cliente manda. Igual à saída hoje, e separado por simetria. */
export type RelatorioRecebimentosQueryEntrada = z.input<typeof relatorioRecebimentosQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Resposta                                                                   */
/* -------------------------------------------------------------------------- */

/** Uma baixa do relatório, com o título dela ao lado. */
export const linhaRelatorioRecebimentosSchema = valoresDaBaixaSchema.extend({
  baixaId: uuidSchema,
  contaReceberId: uuidSchema,
  numeroDocumento: z.string(),
  parcela: z.number().int().positive(),
  totalParcelas: z.number().int().positive(),
  /** O lançamento do título. */
  dataEmissao: dataSchema,
  dataVencimento: dataSchema,
  dataRecebimento: dataSchema,
  clienteId: uuidSchema,
  clienteNome: z.string(),
  contaCorrenteId: uuidSchema,
  contaCorrenteDescricao: z.string(),
  /**
   * O contrato do título: o que ele aponta ou, no título de nota de serviço,
   * o que a nota cita — a mesma leitura do `REC·03` (`.spec/015`, decisão 96).
   */
  contratoId: uuidSchema.nullable(),
  contratoNumero: z.string().nullable(),
  /** A obra de maior índice no rateio do título. `null` sem rateio de obra. */
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  /** Quantas obras o rateio tem **além** da principal — o "(+2)" da coluna. */
  outrasObras: z.number().int().nonnegative(),
});

export type LinhaRelatorioRecebimentos = z.infer<typeof linhaRelatorioRecebimentosSchema>;

/** As baixas de uma data de recebimento, com o subtotal dela. */
export const dataRelatorioRecebimentosSchema = z.object({
  dataRecebimento: dataSchema,
  baixas: z.array(linhaRelatorioRecebimentosSchema),
  totais: totaisDeBaixasSchema,
});

export type DataRelatorioRecebimentos = z.infer<typeof dataRelatorioRecebimentosSchema>;

/** A quebra por empresa. */
export const empresaRelatorioRecebimentosSchema = z.object({
  empresaId: uuidSchema,
  empresaNome: z.string(),
  datas: z.array(dataRelatorioRecebimentosSchema),
  totais: totaisDeBaixasSchema,
});

export type EmpresaRelatorioRecebimentos = z.infer<typeof empresaRelatorioRecebimentosSchema>;

/** Uma linha do resumo por cliente: a baixa inteira vai ao cliente do título. */
export const clienteResumoRecebimentosSchema = totaisDeBaixasSchema.extend({
  clienteId: uuidSchema,
  clienteNome: z.string(),
});

export type ClienteResumoRecebimentos = z.infer<typeof clienteResumoRecebimentosSchema>;

export const resumoRelatorioRecebimentosSchema = z.object({
  porCliente: z.array(clienteResumoRecebimentosSchema),
  porContaCorrente: z.array(contaCorrenteResumoDeBaixasSchema),
});

export type ResumoRelatorioRecebimentos = z.infer<typeof resumoRelatorioRecebimentosSchema>;

/**
 * O relatório inteiro, com o filtro **resolvido** (nomes da empresa, do cliente
 * e da conta corrente) para o cabeçalho do PDF. Só as empresas **com** baixa.
 */
export const relatorioRecebimentosSchema = z.object({
  empresaId: uuidSchema.nullable(),
  empresaNome: z.string().nullable(),
  clienteId: uuidSchema.nullable(),
  clienteNome: z.string().nullable(),
  contaCorrenteId: uuidSchema.nullable(),
  contaCorrenteDescricao: z.string().nullable(),
  recebimentoDe: dataSchema.nullable(),
  recebimentoAte: dataSchema.nullable(),
  empresas: z.array(empresaRelatorioRecebimentosSchema),
  totais: totaisDeBaixasSchema,
  resumo: resumoRelatorioRecebimentosSchema,
});

export type RelatorioRecebimentos = z.infer<typeof relatorioRecebimentosSchema>;

/* -------------------------------------------------------------------------- */
/* Montagem                                                                   */
/* -------------------------------------------------------------------------- */

/** Uma baixa como o repositório a devolve, com o título, a empresa e a conta ao lado. */
export interface BaixaBrutaRecebimentos extends ValoresBrutosDaBaixa {
  baixaId: string;
  contaReceberId: string;
  empresaId: string;
  empresaNome: string;
  clienteId: string;
  clienteNome: string;
  contratoId: string | null;
  contratoNumero: string | null;
  contaCorrenteId: string;
  contaCorrenteDescricao: string;
  contaCorrenteEmpresaNome: string;
  numeroDocumento: string;
  parcela: number;
  totalParcelas: number;
  dataEmissao: string;
  dataVencimento: string;
  dataRecebimento: string;
}

function paraLinha(
  baixa: BaixaBrutaRecebimentos,
  rateio: readonly RateioBrutoContasReceber[],
): LinhaRelatorioRecebimentos {
  const obras = obrasDoTitulo(rateio);
  const principal = obraPrincipalDoTitulo(obras);

  return {
    baixaId: baixa.baixaId,
    contaReceberId: baixa.contaReceberId,
    numeroDocumento: baixa.numeroDocumento,
    parcela: baixa.parcela,
    totalParcelas: baixa.totalParcelas,
    dataEmissao: baixa.dataEmissao,
    dataVencimento: baixa.dataVencimento,
    dataRecebimento: baixa.dataRecebimento,
    ...valoresDaBaixaBruta(baixa),
    clienteId: baixa.clienteId,
    clienteNome: baixa.clienteNome,
    contaCorrenteId: baixa.contaCorrenteId,
    contaCorrenteDescricao: baixa.contaCorrenteDescricao,
    contratoId: baixa.contratoId,
    contratoNumero: baixa.contratoNumero,
    obraId: principal?.obraId ?? null,
    obraNome: principal?.obraNome ?? null,
    outrasObras: Math.max(obras.length - 1, 0),
  };
}

/** A ordem dentro de uma data: cliente, documento, parcela. */
function compararBaixas(a: LinhaRelatorioRecebimentos, b: LinhaRelatorioRecebimentos): number {
  return (
    COMPARAR_TEXTO_BAIXAS.compare(a.clienteNome, b.clienteNome) ||
    COMPARAR_TEXTO_BAIXAS.compare(a.numeroDocumento, b.numeroDocumento) ||
    a.parcela - b.parcela ||
    a.baixaId.localeCompare(b.baixaId)
  );
}

/** O resumo por cliente: a baixa inteira vai ao cliente do título. */
function resumoPorCliente(
  linhas: readonly LinhaRelatorioRecebimentos[],
): ClienteResumoRecebimentos[] {
  const porCliente = new Map<string, LinhaRelatorioRecebimentos[]>();
  for (const linha of linhas) {
    const doCliente = porCliente.get(linha.clienteId) ?? [];
    doCliente.push(linha);
    porCliente.set(linha.clienteId, doCliente);
  }

  return [...porCliente]
    .map(([clienteId, baixas]) => ({
      clienteId,
      clienteNome: (baixas[0] as LinhaRelatorioRecebimentos).clienteNome,
      ...somarValoresDaBaixa(baixas),
    }))
    .sort((a, b) => COMPARAR_TEXTO_BAIXAS.compare(a.clienteNome, b.clienteNome));
}

/**
 * Monta o relatório: quebra por empresa, agrupa por data de recebimento, fecha
 * os subtotais e os dois resumos. Regra pura, no contrato, com teste sem banco.
 */
export function montarRelatorioRecebimentos(
  baixas: readonly BaixaBrutaRecebimentos[],
  rateios: readonly RateioBrutoContasReceber[],
): Pick<RelatorioRecebimentos, 'empresas' | 'totais' | 'resumo'> {
  const rateioPorTitulo = new Map<string, RateioBrutoContasReceber[]>();
  for (const fatia of rateios) {
    const doTitulo = rateioPorTitulo.get(fatia.contaReceberId) ?? [];
    doTitulo.push(fatia);
    rateioPorTitulo.set(fatia.contaReceberId, doTitulo);
  }

  const linhas: LinhaRelatorioRecebimentos[] = [];
  const porEmpresa = new Map<string, { nome: string; linhas: LinhaRelatorioRecebimentos[] }>();
  for (const baixa of baixas) {
    const linha = paraLinha(baixa, rateioPorTitulo.get(baixa.contaReceberId) ?? []);
    linhas.push(linha);
    const empresa = porEmpresa.get(baixa.empresaId) ?? { nome: baixa.empresaNome, linhas: [] };
    empresa.linhas.push(linha);
    porEmpresa.set(baixa.empresaId, empresa);
  }

  const empresas = [...porEmpresa]
    .map(([empresaId, { nome, linhas: daEmpresa }]) => ({
      empresaId,
      empresaNome: nome,
      datas: quebrarPorDataDaBaixa(daEmpresa, (l) => l.dataRecebimento, compararBaixas).map(
        ({ data, baixas: doDia, totais }) => ({ dataRecebimento: data, baixas: doDia, totais }),
      ),
      totais: somarValoresDaBaixa(daEmpresa),
    }))
    .sort((a, b) => COMPARAR_TEXTO_BAIXAS.compare(a.empresaNome, b.empresaNome));

  return {
    empresas,
    totais: somarValoresDaBaixa(linhas),
    resumo: {
      porCliente: resumoPorCliente(linhas),
      porContaCorrente: resumoPorContaCorrente(
        baixas.map((baixa) => ({
          contaCorrenteId: baixa.contaCorrenteId,
          contaCorrenteDescricao: baixa.contaCorrenteDescricao,
          contaCorrenteEmpresaNome: baixa.contaCorrenteEmpresaNome,
          ...valoresDaBaixaBruta(baixa),
        })),
      ),
    },
  };
}
